import './hud.css';
import type { HudViewModel } from './hudModel';

export type HudDisplayMode = 'full' | 'compact' | 'cinematic';

type HudNodeKey =
  | 'horizon'
  | 'altAgl'
  | 'altMsl'
  | 'groundSpeed'
  | 'verticalSpeed'
  | 'heading'
  | 'throttle'
  | 'armed'
  | 'mode'
  | 'battery'
  | 'batteryFill'
  | 'batteryWarning'
  | 'crash'
  | 'lap'
  | 'companionState'
  | 'selectedTrack'
  | 'slantRange'
  | 'timeToStation'
  | 'confirmedCount'
  | 'frustum'
  | 'minSeparation';

const addClass = <T extends HTMLElement>(element: T, ...names: string[]): T => {
  element.classList.add(...names);
  return element;
};

/** Thin retained-mode DOM binding. All nodes are allocated in the constructor. */
export class HudOverlay {
  readonly root: HTMLElement;

  private readonly flightCore: HTMLElement;
  private readonly flightDetail: HTMLElement;
  private readonly mission: HTMLElement;
  private readonly nodes: Record<HudNodeKey, HTMLElement>;

  constructor(documentRef?: Document) {
    const dom = documentRef ?? (typeof document === 'undefined' ? undefined : document);
    if (dom === undefined) {
      throw new Error(
        'HudOverlay requires a DOM document; inject one when running outside a browser',
      );
    }

    this.root = addClass(dom.createElement('div'), 'hud-overlay');
    this.flightCore = addClass(dom.createElement('section'), 'hud-group', 'hud-flight-core');
    this.flightDetail = addClass(dom.createElement('section'), 'hud-group', 'hud-flight-detail');
    this.mission = addClass(dom.createElement('section'), 'hud-group', 'hud-mission');

    const horizon = addClass(dom.createElement('div'), 'hud-horizon-line');
    const attitude = addClass(dom.createElement('div'), 'hud-attitude');
    attitude.appendChild(horizon);
    this.flightCore.appendChild(attitude);

    const value = (parent: HTMLElement, className: string, label?: string): HTMLElement => {
      const row = addClass(dom.createElement('div'), 'hud-row');
      if (label !== undefined) {
        const labelNode = addClass(dom.createElement('span'), 'hud-label');
        labelNode.textContent = label;
        row.appendChild(labelNode);
      }
      const output = addClass(dom.createElement('span'), 'hud-value', className);
      row.appendChild(output);
      parent.appendChild(row);
      return output;
    };

    const heading = value(this.flightCore, 'hud-heading', 'HDG');
    const armed = value(this.flightCore, 'hud-armed');
    const mode = value(this.flightCore, 'hud-mode');
    const lap = value(this.flightCore, 'hud-lap', 'LAP');

    const altAgl = value(this.flightDetail, 'hud-alt-agl', 'AGL');
    const altMsl = value(this.flightDetail, 'hud-alt-msl', 'MSL');
    const groundSpeed = value(this.flightDetail, 'hud-ground-speed', 'GS');
    const verticalSpeed = value(this.flightDetail, 'hud-vertical-speed', 'VS');
    const throttle = value(this.flightDetail, 'hud-throttle', 'THR');
    const battery = value(this.flightDetail, 'hud-battery', 'BAT');
    const batteryTrack = addClass(dom.createElement('div'), 'hud-battery-track');
    const batteryFill = addClass(dom.createElement('div'), 'hud-battery-fill');
    batteryTrack.appendChild(batteryFill);
    this.flightDetail.appendChild(batteryTrack);
    const batteryWarning = value(this.flightDetail, 'hud-battery-warning');
    batteryWarning.textContent = 'SAG';
    const crash = value(this.flightDetail, 'hud-crash');
    crash.textContent = 'CRASH';

    const companionState = value(this.mission, 'hud-companion-state', 'COMP');
    const selectedTrack = value(this.mission, 'hud-selected-track', 'TRACK');
    const slantRange = value(this.mission, 'hud-slant-range', 'RANGE');
    const timeToStation = value(this.mission, 'hud-time-to-station', 'TTS');
    const confirmedCount = value(this.mission, 'hud-confirmed-count', 'CONF');
    const frustum = value(this.mission, 'hud-frustum', 'CAM');
    const minSeparation = value(this.mission, 'hud-min-separation', 'MIN SEP');

    this.root.appendChild(this.flightCore);
    this.root.appendChild(this.flightDetail);
    this.root.appendChild(this.mission);
    this.nodes = {
      horizon,
      altAgl,
      altMsl,
      groundSpeed,
      verticalSpeed,
      heading,
      throttle,
      armed,
      mode,
      battery,
      batteryFill,
      batteryWarning,
      crash,
      lap,
      companionState,
      selectedTrack,
      slantRange,
      timeToStation,
      confirmedCount,
      frustum,
      minSeparation,
    };
    this.setDisplayMode('full');
  }

  apply(viewModel: HudViewModel): void {
    const { flight, mission } = viewModel;
    this.nodes.altAgl.textContent = flight.altAglText;
    this.nodes.altMsl.textContent = flight.altMslText;
    this.nodes.groundSpeed.textContent = flight.groundSpeedText;
    this.nodes.verticalSpeed.textContent = flight.verticalSpeedText;
    this.nodes.heading.textContent = flight.headingText;
    this.nodes.throttle.textContent = flight.throttlePctText;
    this.nodes.armed.textContent = flight.armedText;
    this.nodes.mode.textContent = flight.modeText;
    this.nodes.battery.textContent = flight.batteryText;
    this.nodes.lap.textContent = flight.lapText;
    this.nodes.companionState.textContent = mission.companionStateText;
    this.nodes.selectedTrack.textContent = mission.selectedTrackText;
    this.nodes.slantRange.textContent = mission.slantRangeText;
    this.nodes.timeToStation.textContent = mission.timeToStationText;
    this.nodes.confirmedCount.textContent = String(mission.confirmedTrackCount);
    this.nodes.frustum.textContent = mission.targetInFrustum ? 'IN VIEW' : 'OUT OF VIEW';
    this.nodes.minSeparation.textContent = mission.minSeparationText;

    const pitchPixels = flight.attitude.pitchRad * 80;
    this.nodes.horizon.style.transform = `rotate(${-flight.attitude.rollRad}rad) translateY(${pitchPixels}px)`;
    this.nodes.batteryFill.style.transform = `scaleX(${Math.min(1, Math.max(0, flight.batteryBarFraction))})`;
    this.nodes.batteryWarning.style.opacity = flight.batteryWarning ? '1' : '0';
    this.nodes.crash.style.opacity = flight.crashFlag ? '1' : '0';
    this.nodes.frustum.style.opacity = mission.targetInFrustum ? '1' : '0.55';
  }

  setDisplayMode(mode: HudDisplayMode): void {
    this.flightCore.hidden = mode === 'cinematic';
    this.flightDetail.hidden = mode !== 'full';
    this.mission.hidden = mode !== 'full';
  }
}
